import {
  DEFAULT_LOCALE,
  isLocale,
  localePath,
  matchLocale,
  splitLocalePath,
  type Locale,
} from '../../shared/locales.js';

/**
 * Choosing the language in the browser.
 *
 * The rule, in order: an explicit choice the visitor has made before, then the languages
 * their browser asks for, then English. Nothing here ever guesses from an IP address:
 * where somebody is has very little to do with what they read.
 *
 * The remembered choice lives in local storage, not a cookie. It is a display preference,
 * it is never sent to the server, and it therefore needs no consent banner.
 */

/** Same `pgb.` prefix as every other key this app owns; see `storage.ts`. */
const STORAGE_KEY = 'pgb.lang';

/** Local storage throws in some privacy modes; a language preference is never worth an exception. */
function safeRead(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function readLanguagePreference(): Locale | null {
  const stored = safeRead();
  return isLocale(stored) ? stored : null;
}

export function writeLanguagePreference(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* Preference not remembered. The page still renders in the requested language. */
  }
}

/** The language to use when there is no locale in the URL, as on a private room page. */
export function preferredLocale(): Locale {
  return readLanguagePreference() ?? matchLocale(navigator.languages) ?? DEFAULT_LOCALE;
}

/**
 * Settle the language for a public page, before hydration.
 *
 * Returns the locale to render. When it returns null the browser is already navigating
 * somewhere else and the caller should not hydrate: rendering the English page first and
 * replacing it a moment later is a visible flash of the wrong language.
 *
 * A locale-prefixed URL is treated as an explicit choice, because the only ways to reach
 * one are the language menu, a bookmark, or a link somebody sent deliberately.
 */
export function resolvePublicLocale(): { locale: Locale; path: string } | null {
  const { locale, path } = splitLocalePath(window.location.pathname);

  if (locale !== DEFAULT_LOCALE) {
    writeLanguagePreference(locale);
    return { locale, path };
  }

  const stored = readLanguagePreference();
  if (stored !== null) {
    if (stored === DEFAULT_LOCALE) return { locale, path };
    redirect(stored, path);
    return null;
  }

  const detected = matchLocale(navigator.languages);
  if (detected === null || detected === DEFAULT_LOCALE) {
    // Remember that English was the right answer, so the detection runs exactly once.
    writeLanguagePreference(DEFAULT_LOCALE);
    return { locale, path };
  }

  writeLanguagePreference(detected);
  redirect(detected, path);
  return null;
}

/**
 * `replace`, not `assign`: the English URL the visitor never asked for should not sit in
 * their history, where the back button would bounce them straight back into the redirect.
 */
function redirect(locale: Locale, path: string): void {
  const { search, hash } = window.location;
  window.location.replace(`${localePath(locale, path)}${search}${hash}`);
}
